import { Link, Theme, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import ReviewContext from "context/ReviewContext";
import { FETCH_STATUS } from "descriptions";
import { observer } from "mobx-react-lite";
import { useContext } from "react";
import { EditReviewData, ELocationLabelName, QrSettingsFormFieldType } from "state/ReviewState";
import { StepCard } from "../StepCard";
import { Form, FormFieldArray, FormInputText, useForm } from "components/Form";
import { reviewStepFormSchema as schema } from "./ReviewStep.schema";
import { ReviewStepFields, ReviewStepForm } from "./ReviewStep.types";
import { SubmitHandler } from "react-hook-form";
import LanguageContext from "context/LanguageContext";

const useStyles = makeStyles()((theme: Theme) => ({
	form: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		paddingBottom: theme.spacing(1),
		gap: theme.spacing(0.5),
		width: "100%",
	},
	inputField: {
		maxWidth: theme.spacing(50),
		[theme.breakpoints.down("sm")]: {
			width: "100%",
		},
		"& .MuiFormLabel-root": {
			color: theme.palette.text.secondary,
		},
	},
}));

interface IProps {
	hash: string;
}

export const ReviewStep = observer(({ hash }: IProps) => {
	const { classes } = useStyles();
	const { getTranslate } = useContext(LanguageContext);
	const {
		setStep,
		fetchEditReview,
		fetchEditReviewService,
		evaluation,
		additionalFields,
		location,
	} = useContext(ReviewContext);

	const policyUrl =
		location.label?.name === ELocationLabelName.Hotmaps
			? "https://hotmaps.me/policy"
			: "https://geojet.io/policy.pdf";

	const nameField = additionalFields.find(({ type }) => type === QrSettingsFormFieldType.Name);
	const contactField = additionalFields.find(
		({ type }) => type === QrSettingsFormFieldType.Contact,
	);
	const descriptionField = additionalFields.find(
		({ type }) => type === QrSettingsFormFieldType.Description,
	);
	const fields = additionalFields.filter(({ type }) => type === QrSettingsFormFieldType.Text);

	const handleSubmit: SubmitHandler<ReviewStepForm> = values => {
		const data: EditReviewData = {
			hash,
			session_id: localStorage.getItem(window.location.pathname) || "",
			author: values[ReviewStepFields.Name] || "",
			contacts: values[ReviewStepFields.Contact] || "",
			review: values[ReviewStepFields.Description] || "",
			custom_data: JSON.stringify(values[ReviewStepFields.Fields]),
		};

		fetchEditReview(data).then(({ status }) => {
			if (status === FETCH_STATUS.DONE) {
				setStep(evaluation === "like" ? 4 : 2);
			}
		});
	};

	const formMethods = useForm<ReviewStepForm>({
		schema: schema({
			nameRequired: Boolean(nameField?.required),
			contactRequired: Boolean(contactField?.required),
			descriptionRequired: Boolean(descriptionField?.required),
		}),
		onSuccess: handleSubmit,
		defaultValues: {
			[ReviewStepFields.Name]: "",
			[ReviewStepFields.Contact]: "",
			[ReviewStepFields.Description]: "",
			[ReviewStepFields.Fields]: fields,
		},
	});

	return (
		<StepCard
			title="reviewPage.badReviewStep.title"
			subTitle="reviewPage.badReviewStep.subTitle"
			onSubmit={formMethods.submit}
			onCancel={() => setStep(0)}
			submitTitle="reviewPage.badReviewStep.submit"
			cancelTitle="reviewPage.badReviewStep.cancel"
			isLoading={fetchEditReviewService.loading === FETCH_STATUS.PENDING}
		>
			<Form {...formMethods} className={classes.form}>
				<FormInputText
					fullWidth
					name={ReviewStepFields.Name}
					label={nameField?.label || "reviewPage.badReviewStep.placeholderName"}
					className={classes.inputField}
					required={nameField?.required}
				/>
				<FormInputText
					fullWidth
					name={ReviewStepFields.Contact}
					label={contactField?.label || "reviewPage.badReviewStep.placeholderContact"}
					className={classes.inputField}
					required={contactField?.required}
				/>
				<FormFieldArray
					hideActions
					name={ReviewStepFields.Fields}
					defaultAppendValue={{ id: "", label: "", value: "", required: false }}
					isHideErrorField
					renderItem={({ getName, field }) => (
						<FormInputText
							fullWidth
							required={field.required}
							label={field.label}
							name={getName(ReviewStepFields.Value)}
							className={classes.inputField}
						/>
					)}
				/>
				<FormInputText
					multiline
					fullWidth
					minRows={10}
					maxRows={10}
					required={descriptionField?.required}
					name={ReviewStepFields.Description}
					className={classes.inputField}
					label={descriptionField?.label || "reviewPage.badReviewStep.placeholderText"}
				/>
				{evaluation === "dislike" && (
					<Typography textAlign="center" mb={1} maxWidth={320}>
						{getTranslate("reviewPage.badReviewStep.policy")}{" "}
						<Link href={policyUrl} target="_blank">
							{getTranslate("reviewPage.badReviewStep.policyLink")}
						</Link>
					</Typography>
				)}
			</Form>
		</StepCard>
	);
});
